import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { timeSlots, appointmentTypes, submitAppointment } from '../mock/mockData';
import { OrderButton } from './OrderButton';

export const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    appointmentType: '',
    preferredTime: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitAppointment(formData);
      if (result.success) {
        setSubmitted(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          appointmentType: '',
          preferredTime: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 rounded-2xl shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-lg text-gray-700 mb-6">
              Your appointment request has been received. We'll contact you within 24 hours to confirm your appointment.
            </p>
            <Button 
              onClick={() => setSubmitted(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Book Another Appointment
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-sky-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Get In <span className="text-blue-600">Touch</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Ready to start your custom apparel project? Send us an email and we'll get back to you 
            within 24 hours to discuss your needs and schedule a consultation.
          </p>
          <div className="mt-8">
            <OrderButton
              label="Start Your Order"
              testid="contact-start-order-button"
              className="px-10 py-5 text-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Email CTA Section */}
          <Card className="p-8 bg-white shadow-xl border-2 border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Us Today</h3>
            
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-lg text-gray-700 mb-8">
                  Choose how you'd like to get started with your custom apparel project:
                </p>
              </div>

              <div className="space-y-4">
                <Button 
                  onClick={() => window.open("https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Consultation Request — Which location? Orangeville / Newmarket / Hamilton&body=Hi Avid Branding,%0D%0A%0D%0AI would like to book a free consultation. A branding advisor will be in touch to confirm.%0D%0A%0D%0APreferred consultation location (choose one): Orangeville / Newmarket / Hamilton%0D%0A%0D%0AName:%0D%0APhone:%0D%0ACompany:%0D%0APreferred Day/Time:%0D%0AProject Details:%0D%0A%0D%0AThank you!", '_blank')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 text-lg font-semibold"
                >
                  Email Us to Book Free Consultation
                </Button>

                <Button 
                  onClick={() => window.open('https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Free Sample Request&body=Hi Avid Branding,%0D%0A%0D%0AI would like to request a free sample with my logo.%0D%0A%0D%0AName:%0D%0ACompany:%0D%0APhone:%0D%0AProject Type:%0D%0A%0D%0APlease let me know the next steps.%0D%0A%0D%0AThank you!', '_blank')}
                  variant="outline"
                  className="w-full border-2 border-sky-600 text-sky-600 hover:bg-sky-50 py-4 text-lg font-semibold"
                >
                  Email Us for Free Sample
                </Button>

                <Button 
                  onClick={() => window.open('https://mail.google.com/mail/?view=cm&fs=1&to=communications@da-12.com&su=Logo Design Submission&body=Hi Avid Branding,%0D%0A%0D%0AI have a logo I would like to use for custom apparel.%0D%0A%0D%0AName:%0D%0ACompany:%0D%0APhone:%0D%0AProject Details:%0D%0A%0D%0AI will attach my logo file to this email.%0D%0A%0D%0AThank you!', '_blank')}
                  variant="outline"
                  className="w-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50 py-4 text-lg font-semibold"
                >
                  Email Us Your Logo
                </Button>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg mt-6">
                <h4 className="font-semibold text-gray-900 mb-2">📧 Direct Email</h4>
                <p className="text-blue-600 font-bold text-lg">communications@da-12.com</p>
                <p className="text-sm text-gray-600 mt-2">
                  We typically respond within 24 hours
                </p>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Details */}
            <Card className="p-8 bg-white shadow-xl border-2 border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-blue-600 font-bold text-lg">communications@da-12.com</p>
                    <p className="text-sm text-gray-600">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-sky-100 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Phone</h4>
                    <p className="text-blue-600 font-bold text-lg">519-938-8959</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Office Location</h4>
                    <p className="text-gray-700">4-246372 Hockley Rd</p>
                    <p className="text-gray-700">Orangeville, ON</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Appointment Hours</h4>
                    <p className="text-gray-700">Monday - Saturday</p>
                    <p className="text-gray-700">9:00 AM - 4:30 PM</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Free Sample CTA */}
            <Card className="p-8 bg-gradient-to-br from-teal-100 to-blue-100 border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Get a Free Sample!</h3>
              <p className="text-gray-700 mb-6">
                Experience our quality firsthand. We'll create a sample with your logo 
                so you can see and feel the difference before placing your order.
              </p>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-gray-600 font-semibold">
                  ✓ No obligation • ✓ See actual quality • ✓ Perfect for testing
                </p>
              </div>
            </Card>

            {/* Quality Promise */}
            <Card className="p-6 bg-blue-600 text-white">
              <h4 className="font-bold text-lg mb-2">Our Promise</h4>
              <p className="text-blue-100">
                Durable, stylish apparel with moisture-wicking options. 
                Quality guaranteed with no minimum order requirements.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};